import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { baseUrl } from "../../config/config";
import CodeEditor from "../../components/coding/CodeEditor";
import TestCasePanel from "../../components/coding/TestCasePanel";
import LanguageSelector from "../../components/coding/LanguageSelector";

// Candidate coding question page.
//
//   /candidate/coding/:questionId          — practice / standalone
//   /candidate/coding/:questionId?interview=42&candidate=100  — interview-context
//
// Backend wiring:
//   GET  /job-posting/coding-questions/{id}/test-cases       visible test cases + hiddenCount
//   POST /job-posting/coding-submissions                     submit (trial=true|false)
//   GET  /job-posting/coding-submissions/{id}                poll until COMPLETED
//
// Telemetry captured for integrity:
//   - pasteCount         increments on every paste event
//   - focusBlurCount     increments on every window blur
//   - timeToFirstKeystroke  ms from mount to first onChange
//   - timeToSubmit       ms from mount to Submit click
const DEFAULT_LANGUAGE = "python";
const DEFAULT_STARTERS = {
  javascript: "// Write your solution here\nfunction solve(input) {\n  return '';\n}\n",
  python: "# Write your solution here\nimport sys\n\ndef solve(input_str):\n    return ''\n\nprint(solve(sys.stdin.read()))\n",
  java: "import java.util.*;\nimport java.io.*;\n\npublic class Main {\n  public static void main(String[] args) throws Exception {\n    BufferedReader br = new BufferedReader(new InputStreamReader(System.in));\n    // your code\n  }\n}\n",
  cpp: "#include <bits/stdc++.h>\nusing namespace std;\nint main() {\n  // your code\n  return 0;\n}\n",
  go: "package main\nimport (\"bufio\"; \"fmt\"; \"os\")\nfunc main() {\n  reader := bufio.NewReader(os.Stdin)\n  _ = reader\n  fmt.Println(\"\")\n}\n",
  ruby: "# Write your solution here\nputs gets&.chomp\n",
  csharp: "using System;\nclass Main {\n  static void Main() {\n    var line = Console.ReadLine();\n    Console.WriteLine(line);\n  }\n}\n",
};

export default function CodingQuestion() {
  const { questionId } = useParams();
  const nav = useNavigate();
  const search = new URLSearchParams(window.location.search);
  const interviewId = search.get("interview");
  const candidateId = search.get("candidate");

  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
  const [code, setCode] = useState(DEFAULT_STARTERS[DEFAULT_LANGUAGE]);
  const [testCases, setTestCases] = useState([]);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [error, setError] = useState(null);

  // Telemetry refs (not React state — we don't render them or want re-renders on update).
  const mountedAtRef = useRef(Date.now());
  const firstKeystrokeRef = useRef(null);
  const pasteCountRef = useRef(0);
  const focusBlurRef = useRef(0);

  useEffect(() => {
    document.title = "Coding question - Ample Hire";
    const onBlur = () => { focusBlurRef.current += 1; };
    window.addEventListener("blur", onBlur);
    return () => window.removeEventListener("blur", onBlur);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    axios
      .get(`${baseUrl}/job-posting/coding-questions/${questionId}/test-cases?includeHidden=false`)
      .then((r) => {
        if (cancelled) return;
        setTestCases(Array.isArray(r.data?.testCases) ? r.data.testCases : []);
        setHiddenCount(r.data?.hiddenCount || 0);
      })
      .catch((e) => { if (!cancelled) setLoadError(e?.message || "Couldn't load test cases"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [questionId]);

  const onCodeChange = (next) => {
    if (firstKeystrokeRef.current == null) {
      firstKeystrokeRef.current = Date.now() - mountedAtRef.current;
    }
    setCode(next ?? "");
  };

  const onPaste = () => { pasteCountRef.current += 1; };

  const switchLanguage = (next) => {
    setLanguage(next);
    setCode(DEFAULT_STARTERS[next] || "");
    setSubmissionResult(null);
  };

  const submit = async (trial = false) => {
    if (submitting) return;
    if (!trial && !window.confirm("Submit final answer? You can't change this after submitting.")) return;
    setSubmitting(true);
    setError(null);
    setSubmissionResult(null);
    try {
      const body = {
        questionId: Number(questionId),
        candidateId: candidateId ? Number(candidateId) : 0,
        interviewId: interviewId ? Number(interviewId) : null,
        language,
        sourceCode: code,
        trial,
        telemetry: {
          pasteCount: pasteCountRef.current,
          focusBlurCount: focusBlurRef.current,
          timeToFirstKeystrokeMs: firstKeystrokeRef.current,
          timeToSubmitMs: Date.now() - mountedAtRef.current,
        },
      };
      const r = await axios.post(`${baseUrl}/job-posting/coding-submissions`, body);
      const id = r.data?.submissionId;
      if (id) pollUntilDone(id);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Submission failed");
      setSubmitting(false);
    }
  };

  const pollUntilDone = async (id) => {
    const deadline = Date.now() + 60000;
    let lastStatus = null;
    while (Date.now() < deadline) {
      try {
        const r = await axios.get(`${baseUrl}/job-posting/coding-submissions/${id}`);
        const sub = r.data?.submission;
        lastStatus = sub?.status;
        setSubmissionResult({ submission: sub, results: r.data?.results || [] });
        if (sub?.status === "COMPLETED" || sub?.status === "ERROR" || sub?.status === "TIMEOUT") {
          setSubmitting(false);
          return;
        }
      } catch (e) {
        // network blip — keep polling until deadline
      }
      await new Promise((res) => setTimeout(res, 2000));
    }
    setError(`Grading didn't complete in time (last status: ${lastStatus || "unknown"})`);
    setSubmitting(false);
  };

  const liveResults = submissionResult?.results || null;
  const completed = submissionResult?.submission?.status === "COMPLETED";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <header className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center gap-3">
        <h1 className="text-sm font-semibold text-slate-100">Coding question #{questionId}</h1>
        <LanguageSelector value={language} onChange={switchLanguage} />
        <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-400">
          {pasteCountRef.current > 0 && (
            <span title="Paste events captured" className="text-amber-400">⚠ {pasteCountRef.current} paste{pasteCountRef.current === 1 ? "" : "s"}</span>
          )}
          {focusBlurRef.current > 0 && (
            <span title="Tab/focus switches" className="text-amber-400">↗ {focusBlurRef.current} blur{focusBlurRef.current === 1 ? "" : "s"}</span>
          )}
          <button onClick={() => nav(-1)} className="text-slate-400 hover:text-slate-200">Close</button>
        </div>
      </header>

      <main className="grid grid-cols-12 gap-3 p-3">
        <div className="col-span-12 lg:col-span-8 flex flex-col gap-3">
          <div className="bg-slate-900 border border-slate-800 rounded overflow-hidden">
            <CodeEditor
              value={code}
              onChange={onCodeChange}
              onPaste={onPaste}
              language={language}
              height="60vh"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => submit(true)}
              disabled={submitting}
              className="px-3 py-1.5 rounded border border-slate-700 text-slate-200 text-xs disabled:opacity-50"
            >
              {submitting && submissionResult?.submission?.isTrial ? "Running…" : "Run sample tests"}
            </button>
            <button
              onClick={() => submit(false)}
              disabled={submitting}
              className="px-3 py-1.5 rounded bg-emerald-600 text-white text-xs font-semibold disabled:opacity-50"
            >
              {submitting ? "Grading…" : "Submit final"}
            </button>
            {error && <span className="text-xs text-red-400 self-center">{error}</span>}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-4 bg-slate-900 border border-slate-800 rounded overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-800 flex items-center justify-between">
            <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Tests</div>
            {submissionResult?.submission?.score != null && (
              <div className="text-xs text-emerald-400 font-mono font-semibold">
                {Number(submissionResult.submission.score).toFixed(1)}%
              </div>
            )}
          </div>
          {loading && <div className="p-4 text-center text-xs text-slate-500">Loading tests…</div>}
          {loadError && <div className="p-4 text-center text-xs text-red-400">{loadError}</div>}
          {!loading && !loadError && (
            <TestCasePanel testCases={testCases} hiddenCount={hiddenCount} results={liveResults} />
          )}

          {completed && (
            <ResultSummary submission={submissionResult.submission} />
          )}
          {submissionResult?.submission?.errorMessage && (
            <div className="p-3 border-t border-slate-800 text-[11px] text-red-400 font-mono whitespace-pre-wrap">
              {submissionResult.submission.errorMessage}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function ResultSummary({ submission }) {
  const passed = submission.passedTestCases ?? 0;
  const total = submission.totalTestCases ?? 0;
  const score = submission.score == null ? "—" : Number(submission.score).toFixed(1);
  return (
    <div className="border-t border-slate-800 px-3 py-3 text-xs">
      <div className="flex items-baseline justify-between">
        <div className="text-slate-400">Final score</div>
        <div className="text-xl font-bold text-emerald-400 tabular-nums">{score}%</div>
      </div>
      <div className="text-[11px] text-slate-500 mt-1">
        {passed} of {total} test cases passed
        {submission.totalRuntimeMs ? ` · ${submission.totalRuntimeMs}ms total` : ""}
      </div>
    </div>
  );
}
